/** Formas das respostas da API do módulo admin e de GET /nucleo/saude. */

export interface SaudeNucleo {
  versao: string;
  postgres: { ok: boolean; versao?: string };
  redis: { ok: boolean };
  worker: { ok: boolean; sha: string | null; ultimo_sinal: string | null };
  fila_24h: { estado: string; total: number }[];
  jobs_24h: { estado: string; total: number }[];
  erros_recentes: { id: string; tipo: string; erro: string | null; tentativas: number; created_at: string }[];
  webhooks_falhos: { id: string; evento: string; status_http: number | null; tentativa: number; updated_at: string; url: string }[];
}

export interface SaudeDetalhe {
  versoes: { app: string; node: string; postgres: string };
  modulos: { slug: string; nome: string; versao: string; instalado: boolean; fixo: boolean }[];
  outbox: { estado: string; total: number; mais_antigo: string | null }[];
  outbox_24h: { estado: string; total: number }[];
  jobs_por_tipo_24h: { tipo: string; estado: string; total: number }[];
  jobs_com_erro: { id: string; tipo: string; erro: string | null; tentativas: number; created_at: string; concluido_em: string | null }[];
  webhooks_falhos: { id: string; evento: string; url: string; tentativa: number; status_http: number | null; resposta: string | null; updated_at: string }[];
  eventos_recebidos_com_erro: { id: string; conector: string; erro: string | null; created_at: string }[];
}

export interface ModuloAdmin {
  slug: string;
  nome: string;
  versao: string;
  fixo: boolean;
  instalado: boolean;
  instalado_em: string | null;
  menu: string;
  abas: string[];
  permissoes: string[];
  depende_de: string[];
  dependentes: string[];
  depende_de_nomes: { slug: string; nome: string }[];
  dependentes_instalados: { slug: string; nome: string }[];
  /** Fase 2 (W8): usa se estiver instalado (não instala junto nem trava a desinstalação). */
  opcional_de_nomes?: { slug: string; nome: string }[];
  /** Pacote 2: módulo opcional que vale só no workspace que o ligou (nasce desligado; o Sandbox nasce ligado). */
  liberado_por_workspace?: boolean;
  /** Ligado neste workspace (os outros módulos: sempre, vale a instalação no tenant). */
  ligado_no_workspace?: boolean;
}

export interface ResultadoInstalacao {
  slug: string;
  instalados: string[];
  dependencias: string[];
}

export interface Workspace {
  id: string;
  nome: string;
  slug: string;
  sandbox: boolean;
  fuso: string;
  moeda: string;
  idioma: string;
  membros: number;
  atual: boolean;
  created_at: string;
}

export interface TenantPlataforma {
  id: string;
  nome: string;
  plano: string;
  estado: 'ativo' | 'suspenso' | 'cancelado';
  creditos: number;
  workspaces: number;
  usuarios: number;
  created_at: string;
}

export interface Membro {
  id: string;
  nome: string;
  /** val1 (28/10): vazio na conta sem e-mail (acesso só pelo nome de usuário). */
  email: string;
  usuario?: string | null;
  sem_email?: boolean;
  /** Val7 (29/10): formas de acesso ativas (efetivas): entra com o e-mail de login e/ou com o nome de usuário. */
  login_email?: boolean;
  login_usuario?: boolean;
  ativo: boolean;
  ultimo_acesso: string | null;
  membro_desde: string;
  papeis: { id: string; nome: string; codigo: string }[];
  empresa_ids: string[];
  /** Fase 2, W2b: exceção por pessoa de "Todas as empresas". */
  consolidado?: boolean;
  /** V2 verticais: as empresas vêm das verticais da ficha de funcionário (Pessoas); aqui não mudam. */
  pela_ficha?: boolean;
  /** U15 F5: modo simples ligado para a pessoa neste workspace. */
  modo_simples?: boolean;
  /** N5 (28/10): estilo de interface fixado pelo administrador (null = não fixado). */
  estilo_fixado?: string | null;
  /** val4 (29/10): conta de demonstração sem login próprio, simulável pelo menu do perfil. */
  usuario_teste?: boolean;
  /** Lista 30/10 item 5: usuário de simulação de um papel (só o administrador da plataforma vê). */
  usuario_simulacao?: boolean;
  /** Val1 (28/10): tem exceção de acesso (ação ou página concedida ou retirada por cima dos papéis). */
  acesso_personalizado?: boolean;
  /** Val6 (29/10): a ficha ligada que quem vê pode abrir (o nome vira link). */
  ficha?: FichaLigada | null;
}

export interface Convite {
  id: string;
  email: string;
  nome: string;
  papel_id: string;
  papel_nome: string;
  expira_em: string;
  expirado: boolean;
  convidado_por: string | null;
  created_at: string;
  /** Review r42 visual (B1): falso quando quem vê não pode reenviar (empresas do convite fora do alcance). Ausente = pode. */
  reenviavel?: boolean;
  /** Val6 (29/10): para editar o convite (empresas nula = todas; nome de usuário escolhido). */
  empresa_ids?: string[] | null;
  usuario?: string | null;
  /** Val6 (29/10): `funcionario` = feito pela ficha do Pessoas; null = convidado pelo Admin. */
  origem?: 'funcionario' | null;
  /** Val6 (29/10): a ficha da pessoa que quem vê pode abrir. */
  ficha?: FichaLigada | null;
}

/** Val6 (29/10): ficha ligada a um convite ou membro (Pessoas > Funcionários ou Cadastros > Pessoas e empresas). */
export interface FichaLigada {
  tipo: 'funcionario' | 'parceiro';
  id: string;
}

export interface Papel {
  id: string;
  nome: string;
  codigo: string;
  regra: string;
  de_fabrica: boolean;
  membros: number;
  permissoes: string[];
  efetivas: string[];
  /** Val1 (papel por sub-sistema): páginas (`<slug>.<aba>`) desmarcadas no papel. */
  paginas_negadas?: string[];
}

export interface Permissao {
  codigo: string;
  modulo: string;
  modulo_nome: string;
  acao: string;
  descricao: string;
}

export interface RegistroAuditoria {
  id: string;
  created_at: string;
  ator_tipo: 'usuario' | 'chave_api' | 'sistema';
  ator_id: string | null;
  ator_nome: string | null;
  ator_prefixo: string | null;
  /** val4 (29/10): feita simulando a conta do ator; quem simulava (o ator real). */
  simulado_por?: string | null;
  simulado_por_nome?: string | null;
  acao: string;
  entidade: string;
  entidade_id: string | null;
  ip: string | null;
  request_id: string | null;
}

export interface LinhaDiff {
  campo: string;
  de: unknown;
  para: unknown;
  tipo: 'adicionado' | 'removido' | 'alterado';
}

export interface DetalheAuditoria extends RegistroAuditoria {
  antes: unknown;
  depois: unknown;
  diff: LinhaDiff[];
  /** Review r9 segurança (M1): conteúdo filtrado por permissão (`conteudo` = tudo, `campos` = parte); null = completo. */
  ocultado?: 'conteudo' | 'campos' | null;
}

/** Fase 2 (W37): item do Histórico telas (catálogo do servidor); imagens como `antes/<arquivo>.jpg` ou `depois/<arquivo>.jpg`. */
export interface ItemHistoricoTelas {
  id: string;
  numero?: string;
  titulo: string;
  pedido?: string;
  legenda: string;
  antes: string[];
  depois: string[];
  celular_antes: string[];
  celular_depois: string[];
}

export interface HistoricoTelas {
  disponivel: boolean;
  gerado_em: string | null;
  itens: ItemHistoricoTelas[];
}

export interface Lista<T> {
  itens: T[];
  proximo_cursor: string | null;
}

/**
 * Papéis de fábrica (dono, administrador, gestor, operador, leitura) são traduzidos pelo código;
 * papel personalizado mostra o nome gravado. Quando a API só entrega o nome, o nome de fábrica em português
 * faz o papel de código.
 */
export function nomeDoPapel(t: (chave: string, opcoes?: { defaultValue?: string }) => string, p: { nome: string; codigo?: string; de_fabrica?: boolean }): string {
  if (p.de_fabrica === false) return p.nome;
  const codigo = p.codigo ?? p.nome.trim().toLowerCase();
  return t(`papelFabrica.${codigo}`, { defaultValue: p.nome });
}
